import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState, useRef } from 'react';

export const useDebounceCallback = <CallbackReturnType, CallbackArgs extends unknown[] = []>(
  callback: (...args: CallbackArgs) => CallbackReturnType,
  wait = 100,
): ((...args: CallbackArgs) => void) => {
  const timeout = useRef<NodeJS.Timeout>(null);

  useEffect(() => () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  return useCallback((...args: CallbackArgs) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    timeout.current = setTimeout(() => {
      timeout.current = null;
      callback(...args);
    }, wait);
  }, [callback, wait]);
};

export const useDebounce = <State>(
  initialState: State | (() => State),
  wait?: number,
): [State, Dispatch<SetStateAction<State>>] => {
  const [state, setState] = useState<State>(initialState);
  return [state, useDebounceCallback(setState, wait)];
};

type DebounceRef<CallbackReturnType> = {
  promise: Promise<CallbackReturnType> | void;
  reject: (reason?: unknown) => void;
  resolve: (value?: CallbackReturnType | PromiseLike<CallbackReturnType>) => void;
  timeout: ReturnType<typeof setTimeout>;
};

export const useAsyncDebounce = <CallbackReturnType extends Record<string, unknown> | void, CallbackArgs extends unknown[] = []>(
  callback: (...args: CallbackArgs) => Promise<CallbackReturnType>,
  wait = 100,
): ((...args: CallbackArgs) => Promise<CallbackReturnType>) => {
  const debounceRef = useRef<DebounceRef<CallbackReturnType>>({
    promise: null,
    reject: null,
    resolve: null,
    timeout: null,
  });

  return useCallback(async (...args: CallbackArgs) => {
    if (!debounceRef.current.promise) {
      debounceRef.current.promise = new Promise((resolve, reject) => {
        debounceRef.current.resolve = resolve;
        debounceRef.current.reject = reject;
      });
    }

    if (debounceRef.current.timeout) {
      clearTimeout(debounceRef.current.timeout);
    }

    const pending = debounceRef.current;

    pending.timeout = setTimeout(async () => {
      delete pending.timeout;
      // Detach this record before awaiting the callback so a call that
      // arrives while the request is in flight starts its own promise
      // instead of sharing (and being silently dropped by) this one.
      if (debounceRef.current === pending) {
        debounceRef.current = { promise: null, reject: null, resolve: null, timeout: null };
      }
      try {
        pending.resolve(await callback(...args));
      } catch (err) {
        pending.reject(err);
      }
    }, wait);

    return debounceRef.current.promise;
  }, [callback, wait]);
};